import { useEffect, useState, useCallback } from 'react'
import { startSpan, endSpan } from '@/perf/log'

/** Routes that are part of the shipped product. */
export type ProductRoute =
  | { page: 'home' }
  | { page: 'documents' }
  | { page: 'local'; id: string }

/** Experimental / lab routes. Rendered by src/lab/LabRoutes.tsx, which is code-split out of the product bundle. */
export type LabRoute =
  | { page: 'material-studio' }
  | { page: 'lighting-studio' }
  | { page: 'logo-mock' }
  | { page: 'solid-bench' }
  | { page: 'sdf-bench' }
  | { page: 'paint-bench' }
  | { page: 'swatch-lab' }
  | { page: 'kit-lab' }

export type Route = ProductRoute | LabRoute

const PRODUCT_PAGES: ReadonlySet<Route['page']> = new Set<Route['page']>(['home', 'documents', 'local'])

export function isLabRoute(route: Route): route is LabRoute {
  return !PRODUCT_PAGES.has(route.page)
}

export function parseRoutePath(pathname: string): Route {
  const parts = pathname.replace(/\/+$/, '').split('/').filter(Boolean)
  if (parts.length === 2 && parts[0] === 'local' && parts[1]) {
    return { page: 'local', id: decodeURIComponent(parts[1]) }
  }
  if (parts.length === 1 && parts[0] === 'documents') {
    return { page: 'documents' }
  }
  if (parts.length === 1 && parts[0] === 'material-studio') {
    return { page: 'material-studio' }
  }
  if (parts.length === 1 && parts[0] === 'lighting-studio') {
    return { page: 'lighting-studio' }
  }
  if (parts.length === 1 && parts[0] === 'logo-mock') {
    return { page: 'logo-mock' }
  }
  if (parts.length === 1 && parts[0] === 'solid-bench') {
    return { page: 'solid-bench' }
  }
  if (parts.length === 1 && parts[0] === 'sdf-bench') {
    return { page: 'sdf-bench' }
  }
  if (parts.length === 1 && parts[0] === 'paint-bench') {
    return { page: 'paint-bench' }
  }
  if (parts.length === 1 && parts[0] === 'swatch-lab') {
    return { page: 'swatch-lab' }
  }
  if (parts.length === 1 && parts[0] === 'kit-lab') {
    return { page: 'kit-lab' }
  }
  // '/home-lab' (the home's prototype) and anything unknown land on the home screen.
  return { page: 'home' }
}

export function buildRoutePath(route: Route): string {
  if (route.page === 'local') return `/local/${encodeURIComponent(route.id)}`
  if (route.page === 'documents') return '/documents'
  if (route.page === 'material-studio') return '/material-studio'
  if (route.page === 'lighting-studio') return '/lighting-studio'
  if (route.page === 'logo-mock') return '/logo-mock'
  if (route.page === 'solid-bench') return '/solid-bench'
  if (route.page === 'sdf-bench') return '/sdf-bench'
  if (route.page === 'paint-bench') return '/paint-bench'
  if (route.page === 'swatch-lab') return '/swatch-lab'
  if (route.page === 'kit-lab') return '/kit-lab'
  return '/'
}

export function useRoute(): [Route, (r: Route, replace?: boolean) => void] {
  const [route, setRoute] = useState<Route>(() => parseRoutePath(typeof location !== 'undefined' ? location.pathname : '/'))

  useEffect(() => {
    const onPop = () => setRoute(parseRoutePath(location.pathname))
    const onRoute = () => setRoute(parseRoutePath(location.pathname))
    window.addEventListener('popstate', onPop)
    window.addEventListener('huebo:navigate', onRoute)
    return () => {
      window.removeEventListener('popstate', onPop)
      window.removeEventListener('huebo:navigate', onRoute)
    }
  }, [])

  const navigate = useCallback((r: Route, replace?: boolean) => {
    const span = startSpan('[route] navigate')
    const path = buildRoutePath(r)
    if (replace) history.replaceState(null, '', path)
    else history.pushState(null, '', path)
    // Notify other hook instances that route has changed
    window.dispatchEvent(new Event('huebo:navigate'))
    setRoute(r)
    endSpan(span)
  }, [])

  return [route, navigate]
}
